import { useState, useEffect } from 'react';
import { t } from 'i18next';
import {
  Container,
  DescriptionInput,
  EmptyMessageContainer,
  EmptyMessageText,
  SkillContainer,
} from './styles';
import IconButton from '@mui/material/IconButton';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { SectionTitle } from '../../../../Common/SectionTitle/SectionTitle';
import { Skill } from './Skill';
import FormDialogContainer from '../../../../FormDialogContainer/FormDialogContainer';
import { createSkill, getSkills } from '../../../../../Services/Skills';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { COUNT_ROWS_PER_PAGE } from '../../../../../Types/constants';
import TablePagination from '@mui/material/TablePagination';
import { Loader } from '../../../../Loader/Loader';

interface Props {
  open: boolean;
  onSuccessEdit?: () => void;
  containerWidth?: string;
  paddingTitle?: string;
  preventEdit?: boolean;
  handleClose: () => void;
}

export const SkillsFormDialog = ({
  open,
  handleClose,
  onSuccessEdit,
  containerWidth,
  paddingTitle,
  preventEdit,
}: Props) => {
  const [showInput, setShowInput] = useState(false);
  const [newSkillName, setNewSkillName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const errorMessage = t('toast.errors.something_went_wrong');
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(COUNT_ROWS_PER_PAGE);

  const { mutate: createSkillMutation } = useMutation({
    mutationFn: createSkill,
    onMutate: () => {
      setLoading(true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getSkills'] });
    },
    onSettled: () => {
      setLoading(false);
    },
    onError: () => {
      toast.error(errorMessage);
    },
  });

  const { data: skills, isFetching } = useQuery({
    queryKey: ['getSkills', rowsPerPage, currentPage],
    queryFn: () =>
      getSkills({
        page: currentPage + 1,
        count: rowsPerPage,
      }),
  });

  useEffect(() => {
    if (open) {
      queryClient.invalidateQueries({ queryKey: ['getSkills'] });
    }
  }, [open, queryClient]);

  const handleClick = () => {
    setShowInput(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setNewSkillName(value);

    if (skills?.items?.some(skill => skill.name.toLowerCase() === value.toLowerCase().trim())) {
      setError(t('skills.already_exists'));
    } else {
      setError('');
    }
  };

  const handleConfirm = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (newSkillName && !error) {
      createSkillMutation({ name: newSkillName });
    }
    setShowInput(false);
    setNewSkillName('');
    setError('');
  };

  const handleCancel = () => {
    setShowInput(false);
    setNewSkillName('');
    setError('');
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const renderContent = () => {
    if (loading || isFetching) {
      return <Loader isLoading={true} />;
    }

    if ((skills?.items.length ?? 0) === 0 && !showInput) {
      return (
        <EmptyMessageContainer>
          <EmptyMessageText>{t('actions.no_elem', { elem: 'skills' })}</EmptyMessageText>
        </EmptyMessageContainer>
      );
    }

    return (skills?.items ?? []).map((skill) => (
      <Skill
        key={skill.id}
        skills={skills?.items ?? []}
        skill={skill}
        onSuccessEdit={onSuccessEdit}
        preventEdit={preventEdit}
      />
    ));
  };

  return (
    <FormDialogContainer open={open} handleClose={handleClose}>
      <Container width={containerWidth}>
        <SectionTitle
          title={t('skills.manage')}
          handleAdd={!preventEdit ? () => handleClick() : undefined}
          padding={paddingTitle}
        />
        {showInput && (
          <SkillContainer style={{ display: 'flex', alignItems: 'end', justifyContent: 'space-between' }}>
            <div style={{ width: '100%' }}>
              <DescriptionInput
                value={newSkillName}
                onChange={handleInputChange}
                placeholder={t('skills.enter_name')}
                error={Boolean(error)}
              />
              {error && <span style={{ color: 'red', fontSize: '12px' }}>{error}</span>}
            </div>
            <IconButton onClick={handleConfirm} color="primary" disabled={Boolean(error)}>
              <CheckIcon />
            </IconButton>
            <IconButton onClick={handleCancel} color="primary">
              <CloseIcon />
            </IconButton>
          </SkillContainer>
        )}
        {renderContent()}

        <TablePagination
          rowsPerPageOptions={[5, 10, 15, 30, 50]}
          component="div"
          count={skills?.totalItems ?? 0}
          rowsPerPage={rowsPerPage}
          page={!skills?.totalItems || skills?.totalItems <= 0 ? 0 : currentPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Container>
    </FormDialogContainer>
  );
};